import React, { useEffect, useMemo, useRef, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { DEFAULT_AMENITY_CATEGORIES } from "@/types/amenity";
import {
  AmenityFormData,
  createAmenityFormSchema,
} from "../utils/amenitySchema";
import { FormField } from "@/components/pages/reservations/guests/components/form/FormFields";
import ImageSelector from "@/components/pages/reservations/guests/components/form/ImageSelector";
import TextAreaField from "@/components/common/TextAreaField";
import WeekSchedulePicker from "@/components/common/WeekSchedulePicker";
import { FilterMultiSelect } from "@/components/common/FilterMultiSelect";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Image, PlusCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useHotel } from "@/hooks/useHotel";
import { useAuthenticatedUser } from "@/hooks/useAuthenticatedUser";
import { useAllServices } from "@/hooks/useHotelServices";
import { HotelServiceProviderType } from "@/types/hotelService";
import { useAmenityCategories } from "../hooks/amenities.hooks";
import AmenityCategoryField from "./AmenityCategoryField";
import { isRapidPlan, isStarterPlan } from "@/utils/accessControl";
import { showAmenityFormSubmit } from "../utils/amenityNavigation";
import { getAmenityFormResetKey } from "../utils/amenityFormReset";

const INTERNAL_SERVICE_PROVIDER_TYPES = [
  HotelServiceProviderType.GUEST_SERVICE,
];

interface AmenityFormProps {
  initialValues?: Partial<AmenityFormData>;
  onSubmit: (
    data: AmenityFormData,
    selectedImage: File | null,
  ) => Promise<void>;
  isSubmitting?: boolean;
  submitButtonText?: string;
  titleText?: string;
  readOnly?: boolean;
}

const AmenityForm: React.FC<AmenityFormProps> = ({
  initialValues,
  onSubmit,
  isSubmitting = false,
  submitButtonText = "",
  titleText = "",
  readOnly = false,
}) => {
  const { t } = useTranslation();
  const { userProfile } = useAuthenticatedUser();
  const { hotelId } = useHotel();
  const hideServiceLinking =
    isRapidPlan(userProfile) || isStarterPlan(userProfile);
  const [selectedPhotoFile, setSelectedPhotoFile] = useState<File | null>(null);
  const { data: amenityCategories = [] } = useAmenityCategories(hotelId);
  const { data: allServicesResponse, isLoading: isServicesLoading } =
    useAllServices(
      hotelId || "",
      INTERNAL_SERVICE_PROVIDER_TYPES,
      !hideServiceLinking,
    );

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm<AmenityFormData>({
    resolver: zodResolver(createAmenityFormSchema()),
    defaultValues: {
      category: "",
      isActive: true,
      serviceIds: [],
      schedules: [
        {
          days: [],
          startTime: "",
          endTime: "",
        },
      ],
      ...initialValues,
    },
  });

  const selectedImage = watch("photoId");
  const category = watch("category") ?? "";
  const selectedServiceIds = watch("serviceIds") ?? [];
  const schedules = watch("schedules");
  const categoryOptions = useMemo(
    () =>
      Array.from(
        new Set([
          ...DEFAULT_AMENITY_CATEGORIES,
          ...amenityCategories,
          ...(category ? [category] : []),
        ]),
      ),
    [amenityCategories, category],
  );
  const serviceOptions = useMemo(
    () =>
      (allServicesResponse?.data ?? [])
        .filter(
          (service) =>
            service.providerType === HotelServiceProviderType.GUEST_SERVICE,
        )
        .map((service) => ({
          value: service.id,
          label: service.name,
        })),
    [allServicesResponse?.data],
  );
  const servicesPlaceholder = selectedServiceIds.length
    ? t("amenities.form.servicesSelected", {
        count: selectedServiceIds.length,
      })
    : t("amenities.form.servicesPlaceholder");

  const initialValuesRef = useRef(initialValues);
  initialValuesRef.current = initialValues;
  const resetKey = getAmenityFormResetKey(initialValues);

  useEffect(() => {
    const values = initialValuesRef.current;
    if (values) {
      reset({
        category: "",
        isActive: true,
        serviceIds: [],
        schedules: [
          {
            days: [],
            startTime: "",
            endTime: "",
          },
        ],
        ...values,
      });
    }
  }, [resetKey, reset]);

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    if (file) {
      const imageUrl = URL.createObjectURL(file);
      setValue("photoId", imageUrl);
      setSelectedPhotoFile(file);
    } else {
      setSelectedPhotoFile(null);
      setValue("photoId", undefined);
    }
  };

  const scheduleErrors = (errors.schedules as any)?.map((e: any) => ({
    startTime: e?.startTime?.message as string | undefined,
    endTime: e?.endTime?.message as string | undefined,
    days: e?.days?.message as string | undefined,
  }));

  const submit: SubmitHandler<AmenityFormData> = async (data) => {
    await onSubmit(data, selectedPhotoFile);
  };

  return (
    <div className="mt-10 flex">
      <div className="flex-1 border-r-1 pr-6">
        <h1 className="mb-4 text-center text-2xl font-semibold">
          {titleText || t("amenities.form.title")}
        </h1>
        <form onSubmit={handleSubmit(submit)} className="space-y-6">
          <fieldset disabled={readOnly} className="space-y-6">
            <ImageSelector
              defaultIcon={<Image className="size-[72px] stroke-1" />}
              overlayContent={
                readOnly ? null : (
                  <div className="absolute bottom-0 left-1/2 -translate-x-1/2 rounded-full bg-white">
                    <PlusCircle className="size-5" />
                  </div>
                )
              }
              disabled={readOnly}
              onImageSelect={handleImageUpload}
              selectedImage={selectedImage ?? undefined}
              className="mb-4"
              imageClassName="size-[72px] rounded-full"
            />

            <FormField
              id="name"
              label={t("amenities.form.nameLabel")}
              required
              placeholder={t("amenities.form.namePlaceholder")}
              register={register}
              error={errors.name?.message}
            />

            <AmenityCategoryField
              label={t("amenities.form.category")}
              value={category}
              options={categoryOptions}
              onChange={(value) =>
                setValue("category", value, {
                  shouldDirty: true,
                  shouldValidate: true,
                })
              }
              placeholder={t("amenities.form.categoryPlaceholder")}
              helperText={t("amenities.form.categoryHint")}
              error={errors.category?.message}
            />

            {!hideServiceLinking ? (
              <div className="space-y-2">
                <Label>{t("amenities.form.servicesLabel")}</Label>
                <FilterMultiSelect
                  options={serviceOptions}
                  value={selectedServiceIds}
                  onValueChange={(value) =>
                    setValue("serviceIds", value, {
                      shouldDirty: true,
                    })
                  }
                  placeholder={
                    isServicesLoading
                      ? t("amenities.form.servicesLoading")
                      : servicesPlaceholder
                  }
                  className="w-full justify-between"
                  disabled={readOnly}
                />
                <p className="text-sm text-black/45">
                  {t("amenities.form.servicesHint")}
                </p>
              </div>
            ) : null}

            <TextAreaField
              label={t("amenities.form.descriptionLabel")}
              required
              placeholder={t("amenities.form.descriptionPlaceholder")}
              register={register}
              name="description"
              error={errors.description?.message}
            />

            <WeekSchedulePicker
              value={schedules}
              onChange={(next) => setValue("schedules", next)}
              errors={scheduleErrors}
              disabled={readOnly}
            />
          </fieldset>

          {showAmenityFormSubmit(readOnly) ? (
            <Button
              type="submit"
              variant="new_default"
              size="xl"
              disabled={isSubmitting}
              className="w-full"
            >
              {isSubmitting
                ? t("amenities.form.saving")
                : submitButtonText || t("amenities.form.save")}
            </Button>
          ) : null}
        </form>
      </div>
    </div>
  );
};

export default AmenityForm;
